import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Disclaimer } from "@/components/Common";
import { DocStatusBadge } from "@/components/StatusBadge";
import { DOC_TYPES, DOC_STATUSES } from "@/lib/constants";
import { ArrowLeft, Save, ExternalLink, ShieldCheck, AlertTriangle, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";

// MP8 — Document editor / detail. Same form for new + edit + view (detail page is the editor in read context).
// Includes CISD posture note + related compliance concerns from linked track.

const BLANK = {
  linked_track_id: "",
  linked_request_id: "",
  document_type: "Split Sheet",
  document_status: "Missing",
  file_name: "",
  file_url_or_storage_path: "",
  upload_date: "",
  uploaded_by: "",
  expiration_date: "",
  notes: "",
  internal_only_indicator: true,
  attorney_review_indicator: false,
  archived_offline_indicator: false,
};

export default function DocumentEditor({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ ...BLANK });
  const [meta, setMeta] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [trackCompliance, setTrackCompliance] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tracks").then(r => setTracks(r.data || [])).catch(() => {});
    api.get("/license-requests").then(r => setRequests(r.data || [])).catch(() => {});
    if (mode === "edit" && id) {
      api.get(`/documents/${id}`).then(r => { setData({ ...BLANK, ...r.data }); setMeta(r.data); })
        .catch(e => toast.error(errMsg(e)));
    } else {
      const trackId = searchParams.get("track");
      const requestId = searchParams.get("request");
      if (trackId) setData(d => ({ ...d, linked_track_id: trackId }));
      if (requestId) setData(d => ({ ...d, linked_request_id: requestId }));
    }
  }, [mode, id, searchParams]);

  // Fetch compliance for the linked track to surface concerns.
  useEffect(() => {
    setTrackCompliance(null);
    if (!data.linked_track_id) return;
    api.get(`/tracks/${data.linked_track_id}/compliance`)
      .then(r => setTrackCompliance(r.data))
      .catch(() => {});
  }, [data.linked_track_id]);

  function setField(k, v) { setData(d => ({ ...d, [k]: v })); }

  const linkedTrack = useMemo(
    () => tracks.find(t => t.id === data.linked_track_id) || null,
    [tracks, data.linked_track_id],
  );
  const linkedRequest = useMemo(
    () => requests.find(r => r.id === data.linked_request_id) || null,
    [requests, data.linked_request_id],
  );

  async function save() {
    if (!data.document_type) { toast.error("Document type required"); return; }
    setSaving(true);
    try {
      if (mode === "new") {
        const r = await api.post("/documents", data);
        toast.success("Document created");
        navigate(`/documents/${r.data.id}`, { replace: true });
      } else {
        const r = await api.put(`/documents/${id}`, data);
        toast.success("Saved");
        setMeta(r.data);
        setData({ ...BLANK, ...r.data });
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  }

  async function remove() {
    // Legacy path retained for backwards compatibility.
    // The visible Delete affordance is now <InlineDeleteButton /> below.
  }

  return (
    <div data-testid="document-editor">
      <PageHeader
        title={mode === "new" ? "New Document" : `${data.document_type || "Document"}`}
        subtitle={
          meta
            ? <span className="font-mono text-xs">{meta.id.slice(0, 8)}… · uploaded {(meta.upload_date || meta.created_at || "").slice(0, 10)} by {meta.uploaded_by || "—"}</span>
            : "Internal document tracking"
        }
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/documents")} data-testid="document-back-button"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            {mode === "edit" && (
              <InlineDeleteButton
                endpoint={`/documents/${id}`}
                recordLabel="Document"
                recordId={id}
                isSuperAdmin={user?.role === "super_admin"}
                variant="outline"
                showLabel={true}
                onDeleted={() => navigate("/documents")}
                testid="document-delete"
              />
            )}
            <Button onClick={save} disabled={saving} data-testid="document-save-button" style={{ background: "hsl(347 72% 22%)" }}>
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card data-testid="section-document-core">
            <CardContent className="p-5 grid md:grid-cols-2 gap-3">
              <Field label="Document type">
                <Select value={data.document_type} onValueChange={v => setField("document_type", v)}>
                  <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Document status">
                <Select value={data.document_status} onValueChange={v => setField("document_status", v)}>
                  <SelectTrigger data-testid="document-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Linked track">
                <Select value={data.linked_track_id || "unset"} onValueChange={v => setField("linked_track_id", v === "unset" ? "" : v)}>
                  <SelectTrigger data-testid="document-track-select"><SelectValue placeholder="— None —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">— None —</SelectItem>
                    {tracks.map(t => <SelectItem key={t.id} value={t.id}>{t.track_title} — {t.artist} ({t.track_id})</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Linked license request">
                <Select value={data.linked_request_id || "unset"} onValueChange={v => setField("linked_request_id", v === "unset" ? "" : v)}>
                  <SelectTrigger data-testid="document-request-select"><SelectValue placeholder="— None —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">— None —</SelectItem>
                    {requests.map(r => <SelectItem key={r.id} value={r.id}>{r.request_id} — {r.project_title || "(untitled)"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="File name"><Input value={data.file_name} onChange={e => setField("file_name", e.target.value)} placeholder="auto-populated when a file is uploaded…" data-testid="document-file-name" /></Field>
              <Field full label="File URL or storage path">
                <FileStorageBlock
                  data={data}
                  setField={setField}
                  documentId={id}
                  mode={mode}
                  hasStoredFile={!!meta?.file_storage_path}
                  onUploaded={(payload) => {
                    setMeta((m) => ({ ...(m || {}), ...payload }));
                    setData((d) => ({
                      ...d,
                      file_name: payload.file_name || d.file_name,
                      file_url_or_storage_path: payload.file_url_or_storage_path || d.file_url_or_storage_path,
                    }));
                  }}
                />
              </Field>
              <Field label="Upload date"><Input type="date" value={(data.upload_date || "").slice(0, 10)} onChange={e => setField("upload_date", e.target.value)} data-testid="document-upload-date" /></Field>
              <Field label="Uploaded by"><Input value={data.uploaded_by} onChange={e => setField("uploaded_by", e.target.value)} placeholder="auto-filled on create" data-testid="document-uploaded-by" /></Field>
              <Field label="Expiration date"><Input type="date" value={(data.expiration_date || "").slice(0, 10)} onChange={e => setField("expiration_date", e.target.value)} data-testid="document-expiration-date" /></Field>
              <Field full label="Notes"><Textarea rows={3} value={data.notes} onChange={e => setField("notes", e.target.value)} data-testid="document-notes" /></Field>
            </CardContent>
          </Card>

          <Card data-testid="section-document-indicators">
            <CardContent className="p-5">
              <div className="font-display text-base mb-3">Posture indicators</div>
              <div className="grid md:grid-cols-3 gap-3">
                <Toggle
                  label="Internal-only"
                  checked={!!data.internal_only_indicator}
                  onChange={v => setField("internal_only_indicator", v)}
                  testid="toggle-internal-only"
                  hint="Default for sensitive documents. Never publicly accessible."
                />
                <Toggle
                  label="Attorney review required"
                  checked={!!data.attorney_review_indicator}
                  onChange={v => setField("attorney_review_indicator", v)}
                  testid="toggle-attorney"
                  hint="Flag when rights ambiguity or legal review is needed."
                />
                <Toggle
                  label="Archived offline"
                  checked={!!data.archived_offline_indicator}
                  onChange={v => setField("archived_offline_indicator", v)}
                  testid="toggle-archived"
                  hint="Moved to offline storage per CISD posture."
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right rail — related compliance + CISD note */}
        <div className="space-y-4">
          <Card data-testid="document-related-compliance">
            <CardContent className="p-5">
              <div className="font-display text-base mb-2">Related compliance concerns</div>
              {!linkedTrack ? (
                <div className="text-sm text-muted-foreground">No track linked. Compliance context will appear after linking.</div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div><span className="text-xs text-muted-foreground">Track:</span> <span className="font-medium">{linkedTrack.track_title}</span> <span className="text-xs text-muted-foreground">· {linkedTrack.track_id}</span></div>
                  <div><span className="text-xs text-muted-foreground">Clearance:</span> <DocStatusBadge status={linkedTrack.clearance_status} /></div>
                  {trackCompliance && (
                    <>
                      {(trackCompliance.evaluation?.missing_documents || []).length > 0 && (
                        <div>
                          <div className="text-xs text-muted-foreground mt-1">Track is missing:</div>
                          <ul className="list-disc list-inside text-xs">
                            {trackCompliance.evaluation.missing_documents.slice(0, 6).map((m, i) => <li key={i}>{m}</li>)}
                          </ul>
                        </div>
                      )}
                      {(trackCompliance.evaluation?.risk_flags || []).length > 0 && (
                        <div>
                          <div className="text-xs text-muted-foreground mt-1">Risk flags:</div>
                          <ul className="list-disc list-inside text-xs">
                            {trackCompliance.evaluation.risk_flags.map((m, i) => <li key={i}>{m}</li>)}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                  <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => navigate(`/tracks/${linkedTrack.id}`)} data-testid="document-open-track">
                    Open track <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
              {linkedRequest && (
                <div className="mt-3 pt-3 border-t text-sm">
                  <div className="text-xs text-muted-foreground">Linked request</div>
                  <div className="font-mono text-xs">{linkedRequest.request_id}</div>
                  <div className="text-sm truncate">{linkedRequest.project_title || "(untitled)"}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-[hsl(40_48%_32%)]/30">
            <CardContent className="p-4 flex items-start gap-2 text-xs" data-testid="document-cisd-note">
              <ShieldCheck className="w-4 h-4 mt-0.5" style={{ color: "hsl(40 48% 32%)" }} />
              <div>
                <span className="font-medium">CISD posture:</span> retain only necessary business identifiers in the live system where possible. Sensitive client materials are internal-only and may be archived offline when appropriate.
              </div>
            </CardContent>
          </Card>

          {data.attorney_review_indicator && (
            <Card className="border-[hsl(0_70%_42%)]/40">
              <CardContent className="p-4 flex items-start gap-2 text-xs">
                <AlertTriangle className="w-4 h-4 mt-0.5" style={{ color: "hsl(0 70% 42%)" }} />
                <div>
                  Attorney review flag is on. Route this document through legal review before relying on it for clearance.
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Disclaimer>
        Internal-only · CISD posture: minimize retained sensitive client materials · Not legal advice.
      </Disclaimer>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid, hint }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-md border">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
      </div>
    </div>
  );
}

// FileStorageBlock — replaces the previous raw text input for File URL.
// Shows a functional state machine:
//   1. New (unsaved) document: explanatory admin message; no upload yet.
//   2. Saved document with no file uploaded: upload button + admin message.
//   3. Saved document with a stored file: clickable download link + storage path.
// File name auto-populates from the uploaded file's original filename.
function FileStorageBlock({ data, setField, documentId, mode, hasStoredFile, onUploaded }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const downloadUrl = documentId && hasStoredFile ? `${backendUrl}/api/documents/${documentId}/file` : "";

  async function pickAndUpload(file) {
    if (!documentId) {
      toast.error("Save the document first — a file URL is generated only after a document record exists.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/documents/${documentId}/file`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      onUploaded(r.data || {});
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  }

  return (
    <div className="space-y-2" data-testid="document-file-storage-block">
      <div className="flex items-stretch gap-2">
        <Input
          value={data.file_url_or_storage_path || ""}
          onChange={(e) => setField("file_url_or_storage_path", e.target.value)}
          placeholder={hasStoredFile ? "storage path — auto-populated by upload" : "Upload a file to generate the storage path automatically"}
          className="font-mono text-xs flex-1"
          data-testid="document-file-path"
        />
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              if (!data.file_name) setField("file_name", f.name);
              pickAndUpload(f);
            }
            e.target.value = ""; // allow re-selecting the same file
          }}
          data-testid="document-file-upload-input"
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading || mode === "new" || !documentId}
          onClick={() => inputRef.current?.click()}
          data-testid="document-upload-button"
        >
          <Upload className="w-4 h-4 mr-2" />{uploading ? "Uploading…" : (hasStoredFile ? "Replace file" : "Upload file")}
        </Button>
      </div>
      {mode === "new" && (
        <div className="text-[11px] text-muted-foreground" data-testid="document-file-admin-message">
          Save this document first — the file URL/storage link is generated only after a document record exists. CISD posture: store sensitive files internally only.
        </div>
      )}
      {mode !== "new" && !hasStoredFile && (
        <div className="text-[11px] text-muted-foreground" data-testid="document-file-admin-message">
          No file uploaded yet for this document. Click <span className="font-medium">Upload file</span> to attach one — the storage path will be generated automatically. If you only need to reference an offline / archived asset, write a free-text marker (e.g. <code>offline://archive-2026-batch-04</code>) into the field above.
        </div>
      )}
      {hasStoredFile && downloadUrl && (
        <div className="text-[11px]" data-testid="document-file-download-link-wrapper">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline hover:no-underline"
            style={{ color: "hsl(347 72% 22%)" }}
            data-testid="document-file-download-link"
          >
            <Download className="w-3.5 h-3.5" /> Download stored file
          </a>
          <span className="text-muted-foreground"> · internal access only</span>
        </div>
      )}
    </div>
  );
}
